import type { RefMap } from './list'
import type { Resource } from './resource'

/**
 * A record's activity, as Frappe shows under a document: who created, changed, submitted
 * or cancelled it (with every field change, old and new value) and the comments people
 * left on it. `GET /api/r/<app>/<name>/<id>/activity`.
 */

/** One field of a record that a write changed */
export interface FieldChange {
  field: string
  from: unknown
  to: unknown
}

export interface ActivityItem {
  id: string
  kind: 'audit' | 'comment'
  /** Audit rows: the admin.auditLog action (create, update, submit, cancel…) */
  action?: string
  /** Audit rows: the log's Thai one-line summary */
  summary?: string
  /** Updates: what changed, field by field */
  changes?: FieldChange[]
  /** Updates made while the document was submitted ("edited after submit") */
  afterSubmit?: boolean
  /** Comments: the text */
  body?: string
  userId: string | null
  userName: string
  /** Audit rows: the assistant that did it for them over MCP ("Claude"), shown beside their name */
  via?: string
  /** ISO time it happened */
  at: string
  /** Comments: when it was last edited */
  editedAt?: string | null
}

export interface ActivityResponse {
  /** Newest first */
  items: ActivityItem[]
  /** Labels of ref-field values that appear in `changes` */
  refs: RefMap
  /** The document's input fields changed after it was submitted, and how many edits did it */
  afterSubmit: AfterSubmitEdits
}

export interface AfterSubmitEdits {
  /** Field names, in the order they were first changed */
  fields: string[]
  /** Saves that changed at least one of them */
  edits: number
}

export const MAX_COMMENT_LENGTH = 2000

/**
 * Which input fields of a document were changed after submit: only fields people type
 * (the input schema) count, not what the system sets as the document moves on (status,
 * payment status, current branch…).
 */
export function afterSubmitEdits(resource: Pick<Resource, 'schema'>, items: readonly ActivityItem[]): AfterSubmitEdits {
  const input = new Set(Object.keys(resource.schema.shape))
  const fields: string[] = []
  let edits = 0
  for (const item of [...items].sort((a, b) => a.at.localeCompare(b.at))) {
    if (item.kind !== 'audit' || !item.afterSubmit) continue
    const changed = (item.changes ?? []).map((c) => c.field).filter((f) => input.has(f))
    if (!changed.length) continue
    edits++
    for (const f of changed) if (!fields.includes(f)) fields.push(f)
  }
  return { fields, edits }
}
